import { useState, useEffect, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { OptimizedImage, AnimatedSection, useInView, addResourceHints } from '../components/shared/OptimizedImage';
import { useCMS } from '../context/CMSContext';
import { 
    ArrowRight, 
    Search, 
    MapPin, 
    Sparkles, 
    ChevronRight, 
    Star, 
    TrendingUp, 
    Users, 
    Target, 
    Zap, 
    Globe, 
    Code, 
    HeartPulse, 
    Plane, 
    Building2,
    CheckCircle
} from 'lucide-react';

// Lazy load the booking modal
const BookingModal = lazy(() => import('../components/shared/BookingModal').then(m => ({ default: m.BookingModal })));

const iconMap = {
    'map-pin': MapPin,
    'globe': Globe,
    'sparkles': Sparkles,
    'search': Search,
    'code': Code,
    'zap': Zap,
    'target': Target,
    'trending-up': TrendingUp,
};

const services = [
    {
        icon: MapPin,
        title: 'GMB Optimization',
        description: 'Dominate Google Maps and local search results',
        link: '/gmb-optimization-services',
        featured: true,
        image: 'https://images.unsplash.com/photo-1553484771-371a605b060b?w=600&auto=format&fit=crop&q=80'
    },
    {
        icon: Globe,
        title: 'GEO SEO',
        description: 'Rank for city + service keywords',
        link: '/geo-seo-services',
        image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&auto=format&fit=crop&q=80'
    },
    {
        icon: Sparkles,
        title: 'AI Search Optimization',
        description: 'Get featured in ChatGPT, SGE, Perplexity',
        link: '/ai-search-optimization',
        featured: true,
        image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&auto=format&fit=crop&q=80'
    },
    {
        icon: Search,
        title: 'High-Intent SEO',
        description: 'Target keywords that drive conversions',
        link: '/seo-services',
        image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&auto=format&fit=crop&q=80'
    },
    {
        icon: Code,
        title: 'Programmatic SEO',
        description: 'Scale to 1000+ optimized pages',
        link: '/programmatic-seo',
        image: 'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=600&auto=format&fit=crop&q=80'
    },
];

const industries = [
    { icon: HeartPulse, name: 'Healthcare', link: '/healthcare-seo', image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400&auto=format&fit=crop&q=80' },
    { icon: Building2, name: 'Clinics', link: '/clinic-seo', image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=400&auto=format&fit=crop&q=80' },
    { icon: Plane, name: 'Travel', link: '/travel-seo', image: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=400&auto=format&fit=crop&q=80' },
    { icon: Globe, name: 'Visa Agencies', link: '/visa-agency-seo', image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400&auto=format&fit=crop&q=80' },
    { icon: MapPin, name: 'Local Business', link: '/local-business-seo', image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&auto=format&fit=crop&q=80' },
];

const stats = [
    { value: '300+', label: 'Clients Served' },
    { value: '10M+', label: 'Keywords Ranked' },
    { value: '5x', label: 'Avg. Traffic Growth' },
    { value: '98%', label: 'Client Retention' },
];

const steps = [
    { number: '01', title: 'Discover', description: 'Deep audit of your search presence across Google, Maps, and AI platforms' },
    { number: '02', title: 'Rank', description: 'Strategic execution to dominate high-intent keywords and local results' },
    { number: '03', title: 'Convert', description: 'Turn visibility into revenue with optimized user journeys' },
];

const testimonials = [
    {
        quote: "Aitrixy transformed our online presence. We went from page 5 to position 1 in just 4 months.",
        author: "Dr. Priya Sharma",
        role: "Founder, HealthFirst Clinics",
        rating: 5,
        image: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=100&auto=format&fit=crop&q=80'
    },
    {
        quote: "The ROI has been incredible. Our GMB listing now generates 80% of our leads.",
        author: "Rahul Mehta",
        role: "CEO, TravelWings",
        rating: 5,
        image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&auto=format&fit=crop&q=80'
    },
    {
        quote: "Finally, an agency that delivers results, not just reports. Highly recommend!",
        author: "Anita Gupta",
        role: "Marketing Head, VisaPro",
        rating: 5,
        image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&auto=format&fit=crop&q=80'
    },
];

const caseStudyHighlights = [
    {
        title: 'Healthcare Chain',
        metrics: { traffic: '+340%', leads: '+180%', roi: '8.2x' },
        services: ['GMB', 'Local SEO'],
        image: 'https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=600&auto=format&fit=crop&q=80'
    },
    {
        title: 'Travel Agency',
        metrics: { traffic: '+520%', leads: '+290%', roi: '12x' },
        services: ['SEO', 'AI Search'],
        image: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=600&auto=format&fit=crop&q=80'
    },
    {
        title: 'Visa Consultancy',
        metrics: { traffic: '+280%', leads: '+150%', roi: '6.5x' },
        services: ['GEO SEO', 'GMB'],
        image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&auto=format&fit=crop&q=80'
    },
];

export default function HomePage() {
    const [bookingOpen, setBookingOpen] = useState(false);
    const { getContent, settings } = useCMS();

    // Add resource hints on mount
    useEffect(() => {
        addResourceHints();
    }, []);

    const heroContent = getContent('home', 'hero');
    const statsContent = getContent('home', 'stats') || stats;

    return (
        <div className="relative" data-testid="home-page">
            {/* Hero Section */}
            <section className="relative min-h-[90vh] flex items-center overflow-hidden grid-pattern">
                {/* Background Effects */}
                <div className="hero-glow" />
                <div className="orb orb-1 animate-float" />
                <div className="orb orb-2 animate-float animation-delay-400" />

                <div className="container-custom relative z-10 py-20 md:py-32">
                    <div className="max-w-4xl">
                        {/* Badge */}
                        <AnimatedSection>
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-8">
                                <Sparkles className="w-4 h-4" />
                                <span>{heroContent.badge || "Search Visibility & AI Discovery Agency"}</span>
                            </div>
                        </AnimatedSection>

                        {/* Headline */}
                        <AnimatedSection delay={100}>
                            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.1] mb-6">
                                {heroContent.headline?.split('.').map((part, i) => (
                                    <span key={i}>
                                        {i === 1 ? (
                                            <span className="text-gradient-primary">{part}.</span>
                                        ) : (
                                            part + (i < 2 && part ? '.' : '')
                                        )}
                                        {i === 0 && ' '}
                                    </span>
                                )) || (
                                    <>
                                        Rank on Google.{' '}
                                        <span className="text-gradient-primary">Dominate Maps.</span>{' '}
                                        Show up in AI.
                                    </>
                                )}
                            </h1>
                        </AnimatedSection>

                        {/* Subheadline */}
                        <AnimatedSection delay={200}>
                            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mb-10 leading-relaxed">
                                {heroContent.subheadline || "We don't chase traffic. We capture revenue. Strategic SEO, Maps optimization, and AI search visibility for businesses that want to lead, not follow."}
                            </p>
                        </AnimatedSection>

                        {/* CTAs */}
                        <AnimatedSection delay={300}>
                            <div className="flex flex-col sm:flex-row items-start gap-4">
                                <Button 
                                    size="lg" 
                                    className="btn-glow rounded-full px-8 h-14 text-base"
                                    onClick={() => setBookingOpen(true)}
                                    data-testid="hero-cta-primary"
                                >
                                    {heroContent.cta_primary || "Book Strategy Call"}
                                    <ArrowRight className="w-5 h-5 ml-2" />
                                </Button>
                                <Button 
                                    variant="outline" 
                                    size="lg" 
                                    asChild
                                    className="rounded-full px-8 h-14 text-base border-2"
                                >
                                    <Link to="/case-studies" data-testid="hero-cta-secondary">
                                        {heroContent.cta_secondary || "View Results"}
                                        <ChevronRight className="w-5 h-5 ml-1" />
                                    </Link>
                                </Button>
                            </div>
                        </AnimatedSection>

                        {/* Trust Badges */}
                        <AnimatedSection delay={400}>
                            <div className="flex items-center gap-8 mt-16">
                                <div className="flex -space-x-3">
                                    {testimonials.slice(0, 4).map((t, i) => (
                                        <OptimizedImage 
                                            key={i}
                                            src={t.image}
                                            alt={t.author}
                                            className="w-10 h-10 rounded-full border-2 border-background"
                                            width={40}
                                            height={40}
                                            priority={i < 2}
                                        />
                                    ))}
                                    <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-bold border-2 border-background">
                                        +50
                                    </div>
                                </div>
                                <div className="text-sm">
                                    <div className="flex items-center gap-1 mb-1">
                                        {[1, 2, 3, 4, 5].map((i) => (
                                            <Star key={i} className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                                        ))}
                                    </div>
                                    <span className="text-muted-foreground">300+ businesses trust Aitrixy</span>
                                </div>
                            </div>
                        </AnimatedSection>
                    </div>
                </div>
            </section>

            {/* Stats Bar */}
            <section className="py-12 bg-foreground text-background">
                <div className="container-custom">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
                        {(Array.isArray(statsContent) ? statsContent : stats).map((stat, index) => (
                            <AnimatedSection key={index} delay={index * 100}>
                                <div className="text-center">
                                    <div className="text-3xl md:text-4xl font-bold mb-1">{stat.value}</div>
                                    <div className="text-sm text-background/60">{stat.label}</div>
                                </div>
                            </AnimatedSection>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="section-padding" data-testid="services-section">
                <div className="container-custom">
                    <AnimatedSection>
                        <div className="text-center max-w-3xl mx-auto mb-16">
                            <p className="text-sm font-medium tracking-wider uppercase text-primary mb-4">
                                Our Services
                            </p>
                            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
                                Complete Search Domination
                            </h2>
                            <p className="text-lg text-muted-foreground">
                                From traditional SEO to AI-powered search, we cover every channel where your customers look.
                            </p>
                        </div>
                    </AnimatedSection>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {services.map((service, index) => (
                            <AnimatedSection key={index} delay={index * 100}>
                                <Link
                                    to={service.link}
                                    className={`group relative overflow-hidden rounded-2xl border border-border bg-background hover:border-primary/30 transition-all duration-300 card-hover ${
                                        service.featured ? 'md:col-span-1 lg:row-span-1' : ''
                                    }`}
                                    data-testid={`service-card-${index}`}
                                >
                                    <div className="aspect-[4/3] relative overflow-hidden">
                                        <OptimizedImage
                                            src={service.image}
                                            alt={service.title}
                                            className="w-full h-full"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent" />
                                    </div>
                                    <div className="p-6 relative">
                                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                                            <service.icon className="w-6 h-6 text-primary" />
                                        </div>
                                        <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">{service.title}</h3>
                                        <p className="text-muted-foreground text-sm mb-4">{service.description}</p>
                                        <div className="flex items-center gap-2 text-primary font-medium text-sm">
                                            Learn more
                                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                        </div>
                                    </div>
                                </Link>
                            </AnimatedSection>
                        ))}
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section className="section-padding bg-muted/30" data-testid="how-it-works-section">
                <div className="container-custom">
                    <AnimatedSection>
                        <div className="text-center max-w-3xl mx-auto mb-16">
                            <p className="text-sm font-medium tracking-wider uppercase text-primary mb-4">
                                Our Process
                            </p>
                            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
                                How Aitrixy Works
                            </h2>
                            <p className="text-lg text-muted-foreground">
                                A proven 3-step system to transform your search visibility into revenue.
                            </p>
                        </div>
                    </AnimatedSection>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {steps.map((step, index) => (
                            <AnimatedSection key={index} delay={index * 150}>
                                <div 
                                    className="relative p-8 rounded-2xl bg-background border border-border"
                                    data-testid={`step-${index}`}
                                >
                                    <div className="text-6xl font-bold text-primary/10 mb-4">
                                        {step.number}
                                    </div>
                                    <h3 className="text-2xl font-semibold mb-3">{step.title}</h3>
                                    <p className="text-muted-foreground">{step.description}</p>
                                    {index < steps.length - 1 && (
                                        <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-px bg-border" />
                                    )}
                                </div>
                            </AnimatedSection>
                        ))}
                    </div>
                </div>
            </section>

            {/* Industries */}
            <section className="section-padding" data-testid="industries-section">
                <div className="container-custom">
                    <AnimatedSection>
                        <div className="text-center max-w-3xl mx-auto mb-16">
                            <p className="text-sm font-medium tracking-wider uppercase text-primary mb-4">
                                Industry Expertise
                            </p>
                            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
                                Specialized Solutions
                            </h2>
                            <p className="text-lg text-muted-foreground">
                                Deep expertise in industries where search visibility directly impacts revenue.
                            </p>
                        </div>
                    </AnimatedSection>

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        {industries.map((industry, index) => (
                            <AnimatedSection key={index} delay={index * 80}>
                                <Link
                                    to={industry.link}
                                    className="group relative overflow-hidden rounded-2xl border border-border hover:border-primary/30 bg-background transition-all card-hover"
                                    data-testid={`industry-${index}`}
                                >
                                    <div className="aspect-square relative">
                                        <OptimizedImage
                                            src={industry.image}
                                            alt={industry.name}
                                            className="w-full h-full"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
                                        <div className="absolute bottom-0 left-0 right-0 p-4 text-center">
                                            <div className="w-10 h-10 rounded-xl bg-primary/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-2 group-hover:bg-primary/30 transition-colors">
                                                <industry.icon className="w-5 h-5 text-primary" />
                                            </div>
                                            <h3 className="font-medium text-sm">{industry.name}</h3>
                                        </div>
                                    </div>
                                </Link>
                            </AnimatedSection>
                        ))}
                    </div>
                </div>
            </section>

            {/* Case Study Highlights */}
            <section className="section-padding bg-foreground text-background" data-testid="case-studies-section">
                <div className="container-custom">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
                        <AnimatedSection>
                            <div>
                                <p className="text-sm font-medium tracking-wider uppercase text-primary mb-4">
                                    Results
                                </p>
                                <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">
                                    Real Impact
                                </h2>
                            </div>
                        </AnimatedSection>
                        <AnimatedSection delay={100}>
                            <Button 
                                variant="outline" 
                                asChild
                                className="rounded-full border-background/20 text-background hover:bg-background hover:text-foreground"
                            >
                                <Link to="/case-studies">
                                    View All Case Studies
                                    <ArrowRight className="w-4 h-4 ml-2" />
                                </Link>
                            </Button>
                        </AnimatedSection>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {caseStudyHighlights.map((study, index) => (
                            <AnimatedSection key={index} delay={index * 100}>
                                <div className="group relative overflow-hidden rounded-2xl bg-background/5 border border-background/10">
                                    <div className="aspect-video relative overflow-hidden">
                                        <OptimizedImage
                                            src={study.image}
                                            alt={study.title}
                                            className="w-full h-full group-hover:scale-105 transition-transform duration-500"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-foreground via-foreground/50 to-transparent" />
                                    </div>
                                    <div className="p-6">
                                        <h3 className="text-xl font-semibold mb-4">{study.title}</h3>
                                        <div className="space-y-3 mb-4">
                                            <div className="flex justify-between items-center">
                                                <span className="text-background/60 flex items-center gap-2">
                                                    <TrendingUp className="w-4 h-4" /> Traffic
                                                </span>
                                                <span className="text-lg font-semibold text-green-400">{study.metrics.traffic}</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-background/60 flex items-center gap-2">
                                                    <Users className="w-4 h-4" /> Leads
                                                </span>
                                                <span className="text-lg font-semibold text-green-400">{study.metrics.leads}</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-background/60 flex items-center gap-2">
                                                    <Target className="w-4 h-4" /> ROI
                                                </span>
                                                <span className="text-lg font-semibold text-green-400">{study.metrics.roi}</span>
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            {study.services.map((service, i) => (
                                                <span 
                                                    key={i}
                                                    className="px-3 py-1 rounded-full text-xs font-medium bg-background/10 text-background/80"
                                                >
                                                    {service}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </AnimatedSection>
                        ))}
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="section-padding" data-testid="testimonials-section">
                <div className="container-custom">
                    <AnimatedSection>
                        <div className="text-center max-w-3xl mx-auto mb-16">
                            <p className="text-sm font-medium tracking-wider uppercase text-primary mb-4">
                                Testimonials
                            </p>
                            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">
                                Trusted by Leaders
                            </h2>
                        </div>
                    </AnimatedSection>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {testimonials.map((testimonial, index) => (
                            <AnimatedSection key={index} delay={index * 100}>
                                <div className="p-8 rounded-2xl border border-border bg-background card-hover">
                                    <div className="flex gap-1 mb-6">
                                        {[...Array(testimonial.rating)].map((_, i) => (
                                            <Star key={i} className="w-5 h-5 fill-yellow-500 text-yellow-500" />
                                        ))}
                                    </div>
                                    <p className="text-lg mb-6 leading-relaxed">"{testimonial.quote}"</p>
                                    <div className="flex items-center gap-3">
                                        <OptimizedImage
                                            src={testimonial.image}
                                            alt={testimonial.author}
                                            className="w-12 h-12 rounded-full"
                                            width={48}
                                            height={48}
                                        />
                                        <div>
                                            <div className="font-semibold">{testimonial.author}</div>
                                            <div className="text-sm text-muted-foreground">{testimonial.role}</div>
                                        </div>
                                    </div>
                                </div>
                            </AnimatedSection>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Aitrixy */}
            <section className="section-padding bg-muted/30" data-testid="why-section">
                <div className="container-custom">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <AnimatedSection>
                            <div>
                                <p className="text-sm font-medium tracking-wider uppercase text-primary mb-4">
                                    Why Choose Us
                                </p>
                                <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
                                    Not Just Another SEO Agency
                                </h2>
                                <p className="text-lg text-muted-foreground mb-8">
                                    We're the only agency that combines traditional SEO, Maps optimization, 
                                    and AI search visibility into one unified strategy.
                                </p>

                                <div className="space-y-6">
                                    {[
                                        { icon: Zap, title: 'AI-First Approach', desc: 'Optimized for ChatGPT, SGE, and emerging AI search' },
                                        { icon: MapPin, title: 'Maps Mastery', desc: 'Specialized GMB optimization for local dominance' },
                                        { icon: Target, title: 'Revenue Focus', desc: 'We measure success in leads and sales, not just traffic' },
                                        { icon: TrendingUp, title: 'Proven Results', desc: '300+ businesses scaled with our methodology' },
                                    ].map((item, index) => (
                                        <div key={index} className="flex gap-4">
                                            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                <item.icon className="w-6 h-6 text-primary" />
                                            </div>
                                            <div>
                                                <h4 className="font-semibold mb-1">{item.title}</h4>
                                                <p className="text-muted-foreground">{item.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </AnimatedSection>

                        <AnimatedSection delay={200}>
                            <div className="relative">
                                <div className="aspect-square rounded-3xl bg-gradient-to-br from-primary/20 to-accent/20 p-1">
                                    <div className="w-full h-full rounded-3xl bg-background flex items-center justify-center relative overflow-hidden">
                                        <OptimizedImage
                                            src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&auto=format&fit=crop&q=80"
                                            alt="Team working"
                                            className="absolute inset-0 w-full h-full opacity-20"
                                        />
                                        <div className="text-center p-8 relative z-10">
                                            <div className="text-6xl md:text-8xl font-bold text-gradient-primary mb-4">
                                                3x
                                            </div>
                                            <p className="text-xl text-muted-foreground">
                                                Average ROI in 6 months
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </AnimatedSection>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="section-padding" data-testid="cta-section">
                <div className="container-custom">
                    <AnimatedSection>
                        <div className="relative rounded-3xl overflow-hidden">
                            <OptimizedImage
                                src="https://images.unsplash.com/photo-1553484771-371a605b060b?w=1600&auto=format&fit=crop&q=80"
                                alt="Growth"
                                className="absolute inset-0 w-full h-full"
                            />
                            <div className="absolute inset-0 bg-foreground/90" />
                            <div className="relative p-12 md:p-20 text-center text-background">
                                <h2 className="text-3xl md:text-5xl font-bold mb-6">
                                    Ready to Dominate Search?
                                </h2>
                                <p className="text-lg md:text-xl text-background/70 max-w-2xl mx-auto mb-10">
                                    Join 300+ businesses that chose growth over guesswork. 
                                    Book your free strategy call today.
                                </p>
                                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                                    <Button 
                                        size="lg"
                                        className="bg-background text-foreground hover:bg-background/90 rounded-full px-8 h-14 text-base"
                                        onClick={() => setBookingOpen(true)}
                                        data-testid="final-cta-btn"
                                    >
                                        Book Free Strategy Call
                                        <ArrowRight className="w-5 h-5 ml-2" />
                                    </Button>
                                    <Button 
                                        variant="outline"
                                        size="lg"
                                        asChild
                                        className="rounded-full px-8 h-14 text-base border-background/30 text-background hover:bg-background/10"
                                    >
                                        <a href={`https://wa.me/${settings.whatsapp || '919876543210'}`} target="_blank" rel="noopener noreferrer">
                                            WhatsApp Us
                                        </a>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </AnimatedSection>
                </div>
            </section>

            {/* Booking Modal */}
            <Suspense fallback={null}>
                {bookingOpen && (
                    <BookingModal isOpen={bookingOpen} onClose={() => setBookingOpen(false)} />
                )}
            </Suspense>
        </div>
    );
}
